import React from "react";
import { Box, Chip, Typography } from "@mui/material";
import { Lookup } from "../../redux/aiAssistantApi";

export const SYSTEM_STYLE: Record<
  string,
  { label: string; color: string; short: string }
> = {
  ODOO: { label: "Odoo", color: "#714B67", short: "Od" },
  VMS: { label: "VMS", color: "#1b8a6b", short: "VM" },
  GOOGLE_SHEETS: { label: "Google Sheets", color: "#1e6b3a", short: "GS" },
  PASTEL: { label: "Pastel", color: "#1f6fb5", short: "Pa" },
  CANDY: { label: "Candy", color: "#b5482a", short: "Ca" },
  MS365: { label: "Microsoft 365", color: "#217346", short: "XL" },
};

export const SystemBadge: React.FC<{ system: string; small?: boolean }> = ({
  system,
  small,
}) => {
  const s = SYSTEM_STYLE[system] ?? { label: system, color: "#555" };
  return (
    <Box
      component="span"
      sx={{
        bgcolor: s.color,
        color: "#fff",
        borderRadius: "10px",
        px: 1,
        py: 0.1,
        fontSize: small ? 11 : 12,
        fontWeight: 700,
        whiteSpace: "nowrap",
      }}
    >
      {s.label}
    </Box>
  );
};

export const formatWhen = (iso?: string) =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

const cell = (v: unknown) => {
  if (v === null || v === undefined || v === false) return "";
  if (typeof v === "number") {
    return Number.isInteger(v)
      ? String(v)
      : v.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  // Odoo many2one values arrive as [id, name]
  if (Array.isArray(v) && v.length === 2 && typeof v[0] === "number") {
    return String(v[1]);
  }
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

export const LookupChips: React.FC<{
  lookups: Lookup[];
  selectedId?: string;
  onSelect: (id: string) => void;
}> = ({ lookups, selectedId, onSelect }) => (
  <Box sx={{ display: "flex", gap: 1, overflowX: "auto", pb: 1 }}>
    {lookups.map((l) => (
      <Chip
        key={l.id}
        onClick={() => onSelect(l.id)}
        variant="outlined"
        sx={{
          borderColor:
            selectedId === l.id ? SYSTEM_STYLE[l.system]?.color : "#ccd",
          borderWidth: selectedId === l.id ? 2 : 1,
          bgcolor: "#fff",
          flexShrink: 0,
        }}
        label={
          <Box sx={{ display: "flex", gap: 0.8, alignItems: "center" }}>
            <SystemBadge system={l.system} small />
            <span>
              {l.record.length > 38 ? l.record.slice(0, 38) + "…" : l.record}
            </span>
          </Box>
        }
      />
    ))}
  </Box>
);

export const LookupDetail: React.FC<{ lookup: Lookup }> = ({ lookup }) => {
  const style = SYSTEM_STYLE[lookup.system];
  const columns = Array.from(
    lookup.rows.reduce((set, r) => {
      Object.keys(r).forEach((k) => set.add(k));
      return set;
    }, new Set<string>()),
  ).slice(0, 12);

  return (
    <Box
      sx={{
        border: "1px solid #dde",
        borderRadius: 2,
        bgcolor: "#fff",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          bgcolor: style?.color,
          color: "#fff",
          px: 2,
          py: 1.2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography fontWeight={700} fontSize={14}>
          {style?.label} &nbsp; {lookup.record}
        </Typography>
        <Box
          sx={{
            bgcolor: "#f6c60f",
            color: "#222",
            fontSize: 10,
            fontWeight: 700,
            px: 1,
            borderRadius: 1,
          }}
        >
          LIVE
        </Box>
      </Box>
      <Box
        sx={{
          px: 2,
          py: 0.8,
          fontSize: 12,
          color: "#555",
          borderBottom: "1px solid #eee",
          fontFamily: "monospace",
        }}
      >
        {lookup.asOf
          ? `Last updated ${formatWhen(lookup.asOf)}`
          : `Read ${formatWhen(lookup.retrievedAt)}`}
        {lookup.lastModifiedBy ? ` by ${lookup.lastModifiedBy}` : ""}
      </Box>
      <Box
        component="pre"
        sx={{
          m: 0,
          px: 2,
          py: 1.2,
          bgcolor: "#f7f8fb",
          fontSize: 12,
          whiteSpace: "pre-wrap",
          wordBreak: "break-all",
          borderBottom: "1px solid #eee",
        }}
      >
        {lookup.query}
      </Box>
      {lookup.error ? (
        <Box sx={{ p: 2, color: "#b00020", fontSize: 13 }}>
          Lookup failed: {lookup.error}
        </Box>
      ) : (
        <Box sx={{ overflow: "auto", maxHeight: 420 }}>
          <Box
            component="table"
            sx={{ borderCollapse: "collapse", width: "100%", fontSize: 12.5 }}
          >
            <thead>
              <tr>
                {columns.map((c) => (
                  <Box
                    component="th"
                    key={c}
                    sx={{
                      textAlign: "left",
                      p: 1,
                      borderBottom: "1px solid #ddd",
                      position: "sticky",
                      top: 0,
                      bgcolor: "#fff",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {c}
                  </Box>
                ))}
              </tr>
            </thead>
            <tbody>
              {lookup.rows.map((r, i) => (
                <tr key={i}>
                  {columns.map((c) => (
                    <Box
                      component="td"
                      key={c}
                      sx={{
                        p: 1,
                        borderBottom: "1px solid #f0f0f0",
                        maxWidth: 260,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {cell(r[c])}
                    </Box>
                  ))}
                </tr>
              ))}
            </tbody>
          </Box>
        </Box>
      )}
      <Box
        sx={{
          px: 2,
          py: 1,
          fontSize: 12,
          color: "#666",
          display: "flex",
          justifyContent: "space-between",
          borderTop: "1px solid #eee",
        }}
      >
        <span>
          {lookup.count} record{lookup.count === 1 ? "" : "s"}
          {lookup.truncated ? " (more exist, showing first rows)" : ""}
        </span>
        <span>Read-only</span>
      </Box>
    </Box>
  );
};
